// Home: what the company does in one screen, a way to start searching at
// once, the listings, why buy through them, and the enquiry form.
import { useRef } from 'react';
import { useSite } from '../lib/site-context.jsx';
import { formatPrice, projectHref } from '../lib/format.js';
import { ProjectCard, EmptyState } from '../ui/ProjectCard.jsx';
import { LeadForm } from '../ui/LeadForm.jsx';
import { Icon } from '../ui/Icon.jsx';

function Hero({ projects, options }) {
  const { t } = useSite();
  return (
    <section className="hero">
      <div className="wrap hero-grid">
        <div className="hero-copy">
          <span className="hero-eyebrow">{t('hero_eyebrow', 'Noida · Gurugram · Yamuna Expressway')}</span>
          <h1>{t('hero_title', 'Real estate along the corridors shaping the next NCR.')}</h1>
          <p className="hero-sub">{t('hero_sub', '')}</p>
          <form className="hero-search" action="/projects" method="get" role="search" aria-label={t('hero_search', 'Search projects')}>
            <div className="field">
              <label htmlFor="hs-city">{t('label_corridor', 'Corridor')}</label>
              <select id="hs-city" name="city" defaultValue="">
                <option value="">{t('option_all_corridors', 'All corridors')}</option>
                {options.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="hs-type">{t('label_project_type', 'Project type')}</label>
              <select id="hs-type" name="project_type" defaultValue="">
                <option value="">{t('option_all_types', 'All types')}</option>
                {options.types.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button type="submit" className="btn btn-rust"><Icon name="search" size={18} /> {t('hero_search_btn', 'Search')}</button>
          </form>
          <div className="hero-actions">
            <a href="/projects" className="btn btn-outline">{t('hero_browse', 'Browse Projects')}</a>
            <a href="#enquire" className="btn btn-ghost">{t('hero_talk', 'Talk to Us')}</a>
          </div>
          {projects.length > 0 && <p className="hero-proof">{projects.length} {projects.length === 1 ? 'live project' : 'live projects'} · {projects.filter((p) => p.rera_number).length} RERA registered</p>}
        </div>
        <div className="hero-art" aria-hidden="true">
          <svg className="draw" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path className="skyline" d="M10,230 L10,150 L55,150 L55,190 L95,190 L95,110 L135,110 L135,170 L175,170 L175,125 L215,125 L215,205 L255,205 L255,95 L295,95 L295,175 L335,175 L335,135 L375,135 L375,195 L415,195 L415,115 L455,115 L455,180 L495,180 L495,155 L545,155 L545,230" />
            <path className="expressway" d="M0,345 L230,255 L370,255 L600,345 M60,340 L600,340" />
            <path className="expressway" d="M270,258 L280,258 M300,257 L310,257 M330,257 L340,257" />
            <path className="river" d="M0,368 Q75,345 150,368 T300,368 T450,368 T600,362" />
          </svg>
        </div>
      </div>
    </section>
  );
}

function Stats({ projects }) {
  const { t } = useSite();
  const cities = new Set(projects.map((p) => p.city).filter(Boolean));
  const stats = [
    ['total', projects.length, t('stat_live_projects', 'Live Projects')],
    ['cities', cities.size, t('stat_corridors', 'Corridors Covered')],
    ['ready', projects.filter((p) => p.status === 'Ready to Move').length, t('stat_ready', 'Ready to Move')],
    ['rera', projects.filter((p) => p.rera_number).length, t('stat_rera', 'RERA Registered')],
  ];
  return (
    <section className="stat-strip" aria-label="At a glance">
      <dl className="wrap stat-grid" id="stat-strip">
        {stats.map(([key, value, label]) => (
          <div className="stat" key={key}><dt className="stat-label">{label}</dt><dd className="stat-value" data-stat={key}>{value}</dd></div>
        ))}
      </dl>
    </section>
  );
}

function Spotlight({ project: p }) {
  const { t } = useSite();
  if (!p) return null;
  return (
    <section className="section" id="spotlight-section">
      <div className="wrap">
        <span className="section-eyebrow">{t('eyebrow_spotlight', 'Spotlight')}</span>
        <div className="spotlight" id="spotlight-block">
          <div className="spotlight-media">
            {p.hero_image && <img src={p.hero_image} alt={p.name} loading="lazy" decoding="async" width="960" height="640" />}
            <span className="spotlight-badge">{p.project_type === 'Ultra-Luxury' ? 'Ultra-Luxury Spotlight' : 'Spotlight'}</span>
            <span className="spotlight-media-caption">{[p.sector, p.city].filter(Boolean).join(' · ')}</span>
          </div>
          <div className="spotlight-body">
            <h2>{p.name}</h2>
            <p>{p.tagline}</p>
            <dl className="spotlight-meta">
              <div><dt>Price band</dt><dd>{formatPrice(p)}</dd></div>
              <div><dt>Status</dt><dd>{p.status}</dd></div>
              <div><dt>Possession</dt><dd>{p.possession_date || 'TBA'}</dd></div>
            </dl>
            <a className="btn btn-rust" href={projectHref(p)}>Explore {p.name}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Corridors({ options }) {
  const { t } = useSite();
  const known = { Noida: 'chip_noida', Gurugram: 'chip_gurugram', 'Yamuna Expressway': 'chip_yamuna' };
  const typeKey = { Plot: ['chip_yida', 'YIDA Plots'], 'Ultra-Luxury': ['chip_luxury', 'Ultra-Luxury'] };
  return (
    <section className="section section-tight">
      <div className="wrap">
        <div className="section-head">
          <div>
            <span className="section-eyebrow">{t('eyebrow_browse_corridor', 'Browse by corridor')}</span>
            <h2>{t('heading_find_stretch', 'Find your stretch of the NCR')}</h2>
          </div>
        </div>
        <div className="chip-row">
          {options.cities.map((c) => <a key={c} className="chip" href={`/projects?city=${encodeURIComponent(c)}`}>{known[c] ? t(known[c], c) : c}</a>)}
          {options.types.filter((x) => typeKey[x]).map((x) => <a key={x} className="chip" href={`/projects?project_type=${encodeURIComponent(x)}`}>{t(typeKey[x][0], typeKey[x][1])}</a>)}
        </div>
      </div>
    </section>
  );
}

function Featured({ projects }) {
  const { t } = useSite();
  const track = useRef(null);
  const items = projects.filter((p) => p.show_in_slider);
  const scroll = (dir) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector('.slider-item')?.offsetWidth || 320;
    el.scrollBy({ left: dir * (card + 16), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  return (
    <section className="section">
      <div className="wrap">
        <div className="section-head">
          <div>
            <span className="section-eyebrow">{t('eyebrow_listings', 'Current listings')}</span>
            <h2>{t('heading_featured', 'Featured projects')}</h2>
          </div>
          <a href="/projects" className="btn btn-outline btn-sm">{t('view_all_projects', 'View all projects')}</a>
        </div>
        {items.length === 0
          ? <EmptyState>No projects are published yet. Check back soon.</EmptyState>
          : (
            <div className="slider-wrap">
              <ul className="project-slider" id="project-slider" ref={track} tabIndex={0} aria-label="Featured projects">
                {items.map((p, i) => <li className="slider-item" key={p.id}><ProjectCard project={p} priority={i < 2} badge={i === 0 ? 'Top Pick' : undefined} /></li>)}
              </ul>
              {items.length > 1 && (
                <div className="slider-controls" id="slider-controls">
                  <button type="button" className="slider-btn" id="slider-prev" aria-label="Previous project" onClick={() => scroll(-1)}><Icon name="arrow" className="flip" /></button>
                  <button type="button" className="slider-btn" id="slider-next" aria-label="Next project" onClick={() => scroll(1)}><Icon name="arrow" /></button>
                </div>
              )}
            </div>
          )}
      </div>
    </section>
  );
}

function Why() {
  const { t, settings } = useSite();
  const company = settings.company_name || 'KeysGrove';
  const reasons = [
    ['reason1_title', 'RERA-first listings', 'reason1_body', `Every project on ${company} is published with its RERA registration number, verified before it goes live.`],
    ['reason2_title', 'Corridor expertise', 'reason2_body', 'We track Noida, Gurugram and the Yamuna Expressway closely, including YIDA notifications and infrastructure timelines.'],
    ['reason3_title', 'No brokerage games', 'reason3_body', 'Transparent pricing bands, unit types and possession dates. No bait-and-switch listings.'],
    ['reason4_title', 'One enquiry, direct response', 'reason4_body', 'Your enquiry reaches our desk directly. No call-centre relay, no resale of your details.'],
  ];
  return (
    <section className="section section-band" id="why-keysgrove">
      <div className="wrap">
        <div className="section-head">
          <div>
            <span className="section-eyebrow">{t('eyebrow_why_buy', 'Why buy through us')}</span>
            <h2>{t('heading_why_buy', `Why buy through ${company}`).replace('KeysGrove', company)}</h2>
          </div>
        </div>
        <ol className="reasons-grid">
          {reasons.map(([tk, title, bk, body], i) => (
            <li className="reason" key={tk}>
              <span className="reason-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <h3>{t(tk, title)}</h3>
              <p>{t(bk, body).replace(/KeysGrove/g, company)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Enquire({ responseMinutes }) {
  const { t } = useSite();
  const label = responseMinutes == null ? '' : responseMinutes < 60 ? `${responseMinutes} minutes` : `${Math.round(responseMinutes / 60)} hours`;
  return (
    <section className="section" id="enquire">
      <div className="wrap enquire-wrap">
        <div className="section-head">
          <div>
            <span className="section-eyebrow">{t('eyebrow_get_in_touch', 'Get in touch')}</span>
            <h2>{t('heading_tell_us', "Tell us what you're looking for")}</h2>
            {label && <div id="response-time-badge" className="response-time-badge">We typically respond within {label}</div>}
          </div>
        </div>
        <LeadForm budget successText="Thank you — our team will reach out shortly." />
      </div>
    </section>
  );
}

export default function Home({ data }) {
  const { projects = [], options, responseMinutes } = data;
  const featured = projects.find((p) => p.is_featured);
  return (
    <>
      <Hero projects={projects} options={options} />
      <Stats projects={projects} />
      <Spotlight project={featured} />
      <Corridors options={options} />
      <Featured projects={projects} />
      <Why />
      <Enquire responseMinutes={responseMinutes} />
    </>
  );
}
